import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenantCode, withTenant } from '../tenant';
import { WizardPreviewModal } from './WizardPreviewModal';
import { EyeOutlined, SyncOutlined, DeleteOutlined } from '@ant-design/icons';

const MAX_SLOTS = 5;
const allowedTypes = new Set(['image/jpeg', 'image/png']);

export function WizardPage() {
  const navigate = useNavigate();
  const tenantCode = getTenantCode();
  const [tenantName, setTenantName] = React.useState('');
  const [tenantLogo, setTenantLogo] = React.useState('');
  const [wizardData, setWizardData] = React.useState({ captions: [], imageUrls: [] });
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [slots, setSlots] = React.useState(
    Array.from({ length: MAX_SLOTS }, () => ({
      file: null,
      caption: '',
      existingUrl: '',
    }))
  );
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');

  const loadWizardData = React.useCallback(async () => {
    if (!tenantCode) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(withTenant('/api/admin/wizard'));
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile caricare i dati wizard.');
      }
      const data = await res.json();
      const captions = Array.isArray(data?.captions) ? data.captions : [];
      const imageUrls = Array.isArray(data?.imageUrls) ? data.imageUrls : [];
      setWizardData({ captions, imageUrls });
      setSlots((prev) =>
        prev.map((slot, idx) => ({
          ...slot,
          caption: captions[idx] || '',
          existingUrl: imageUrls[idx] || '',
          file: null,
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [tenantCode]);

  React.useEffect(() => {
    loadWizardData();
  }, [loadWizardData]);

  React.useEffect(() => {
    const loadTenantName = async () => {
      if (!tenantCode) return;
      try {
        const res = await fetch('/api/admin/tenants', { method: 'GET' });
        if (!res.ok) return;
        const tenants = await res.json().catch(() => []);
        if (!Array.isArray(tenants)) return;
        const match = tenants.find((t) => String(t.id).toLowerCase() === String(tenantCode).toLowerCase());
        if (!match) return;
        const label = match.displayName || match.name || '';
        setTenantName(label);
        setTenantLogo(match.logoBase64 || match.logoUrl || '');
      } catch {
        // ignore
      }
    };
    loadTenantName();
  }, [tenantCode]);

  const updateCaption = (index, value) => {
    setSlots((prev) => prev.map((slot, idx) => (
      idx === index ? { ...slot, caption: value } : slot
    )));
  };

  const updateFile = (index, file) => {
    if (file && !allowedTypes.has(file.type)) {
      setError('Formato non valido. Solo JPG o PNG.');
      return;
    }
    setError('');
    setSlots((prev) => prev.map((slot, idx) => (
      idx === index ? { ...slot, file } : slot
    )));
  };

  const handleSubmit = async () => {
    if (!tenantCode) {
      setError('Tenant mancante: specificare ?cod=<GUID>.');
      return;
    }
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const form = new FormData();
      slots.forEach((slot, idx) => {
        const position = idx + 1;
        form.append(`caption${position}`, slot.caption || '');
        if (slot.file) {
          form.append(`photo${position}`, slot.file, slot.file.name);
        }
      });

      const res = await fetch(withTenant('/api/admin/wizard'), {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Upload wizard non riuscito.');
      }
      await res.json().catch(() => ({}));
      setMessage('Wizard aggiornato con successo.');
      await loadWizardData();
      window.close();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWizard = async () => {
    if (!tenantCode) return;
    const ok = window.confirm('Eliminare tutte le foto e le didascalie del wizard?');
    if (!ok) return;
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch(withTenant('/api/admin/wizard'), { method: 'DELETE' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Elimina wizard non riuscito.');
      }
      setMessage('Wizard eliminato.');
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const isWizardComplete = React.useMemo(() => {
    if (!wizardData) return false;
    const captions = wizardData.captions || [];
    const images = wizardData.imageUrls || [];
    if (captions.length < MAX_SLOTS || images.length < MAX_SLOTS) return false;
    return captions.every((c) => String(c || '').trim()) && images.every((u) => String(u || '').trim());
  }, [wizardData]);

  if (!tenantCode) {
    return (
      <div className="wizard-shell">
        <div className="wizard-card">
          <h1>Wizard immagini</h1>
          <p className="muted">Specificare il tenant con il parametro <span className="mono">cod</span>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-shell">
      <div className="wizard-card">
        <header className="wizard-header">
          <div>
            <h1>Wizard immagini</h1>
            <p className="muted">
              Tenant: <span className="mono">{tenantName || tenantCode}</span>
            </p>
          </div>
          <div className="wizard-actions">
            <button className="secondary-button" onClick={handleCancel} disabled={loading}>
              Annulla
            </button>
            <button
              className="secondary-button"
              onClick={() => {
                if (!isWizardComplete) {
                  setError('Preview disponibile solo con 5 foto e 5 didascalie complete.');
                  return;
                }
                setError('');
                setPreviewOpen(true);
              }}
              disabled={loading}
            >
              <EyeOutlined /> Preview
            </button>
            <button className="secondary-button" onClick={loadWizardData} disabled={loading}>
              <SyncOutlined /> Aggiorna
            </button>
            <button className="primary-button" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva'}
            </button>
            <button className="danger-button" onClick={handleDeleteWizard} disabled={loading}>
              <DeleteOutlined /> Elimina wizard
            </button>
          </div>
        </header>

        {message && <div className="info-banner">{message}</div>}
        {error && <div className="error-banner">{error}</div>}
        {loading && <div className="loading-inline">Operazione in corso...</div>}

        <div className="wizard-grid">
          {slots.map((slot, idx) => (
            <div key={idx} className="wizard-slot">
              <div className="wizard-slot-header">
                <strong>Foto {idx + 1}</strong>
              </div>
              <div className="wizard-preview">
                {slot.file ? (
                  <img src={URL.createObjectURL(slot.file)} alt={`Preview ${idx + 1}`} />
                ) : slot.existingUrl ? (
                  <img src={slot.existingUrl} alt={`Foto ${idx + 1}`} />
                ) : (
                  <span className="muted">Nessuna immagine</span>
                )}
              </div>
              <input
                className="tenant-input"
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => updateFile(idx, e.target.files?.[0] || null)}
                disabled={loading}
              />
              <textarea
                className="wizard-textarea"
                placeholder="Didascalia"
                value={slot.caption}
                onChange={(e) => updateCaption(idx, e.target.value)}
                rows={3}
                disabled={loading}
              />
            </div>
          ))}
        </div>
      </div>
      <WizardPreviewModal
        open={previewOpen}
        imageUrls={wizardData.imageUrls || []}
        captions={wizardData.captions || []}
        tenantName={tenantName}
        tenantLogo={tenantLogo}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}
